/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Users, Building2, Award, Target, UserX, Settings2, GripVertical, Eye, EyeOff, CheckCircle2, X, Check, Plus, BarChart2, Megaphone, BookOpen, AlertTriangle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Drawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/Page';
import { getDashboardStats } from '../../../services/clientAdmin/dashboardServices';

const STORAGE_KEY = 'clientDashboardConfig';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ProgressBar({ value, max = 100, color = 'bg-purple-500' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden"><div className={`h-2 ${color} rounded-full`} style={{ width: `${pct}%` }} /></div>;
}
function formatDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function timeAgo(d) { if (!d) return ''; const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000); if (diff < 1) return 'Just now'; if (diff < 60) return `${diff}m ago`; if (diff < 1440) return `${Math.floor(diff / 60)}h ago`; return `${Math.floor(diff / 1440)}d ago`; }

// ─── Widgets ──────────────────────────────────────────────────────────────────

function StatsCards({ data }) {
  const remaining = data.contractEndDate ? Math.max(0, Math.ceil((new Date(data.contractEndDate) - Date.now()) / 86400000)) : 0;
  const isExpired = data.contractEndDate && new Date(data.contractEndDate) < new Date();
  const displayStatus = isExpired ? 'Inactive' : (data.subscriptionStatus || 'N/A');
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[
        { l: 'Active Learners', v: data.activeLearners, icon: Users, c: 'bg-blue-50 text-blue-600', },
        { l: 'Departments', v: data.departments, icon: Building2, c: 'bg-purple-50 text-purple-600', s: `${data.teams} teams` },
        { l: 'Avg Score', v: data.topPerformers?.[0] ? `${data.topPerformers[0].avgScore}%` : '—', icon: Target, c: 'bg-green-50 text-green-600' },
        { l: 'Licence', v: `${data.usedSeats}/${data.totalSeats}`, icon: Award, c: 'bg-amber-50 text-amber-600', s: `${displayStatus} · ${remaining}d left` },
      ].map((k, i) => (
        <div key={i} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`${k.c} rounded-lg p-2.5`}><k.icon size={18} /></div>
            <div><p className="text-xl font-bold text-gray-900">{k.v}</p><p className="text-xs text-gray-500">{k.l}</p>{k.s && <p className="text-xs text-gray-400">{k.s}</p>}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LicenceWidget({ data }) {
  const { totalSeats, usedSeats, subscriptionStatus, contractEndDate } = data;
  const pct = totalSeats ? Math.round((usedSeats / totalSeats) * 100) : 0;
  const remaining = contractEndDate ? Math.max(0, Math.ceil((new Date(contractEndDate) - Date.now()) / 86400000)) : 0;
  const isExpired = contractEndDate && new Date(contractEndDate) < new Date();
  const displayStatus = isExpired ? 'Inactive' : (subscriptionStatus || 'N/A');
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 h-full">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Licence</p>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isExpired ? 'bg-red-100 text-red-700' : subscriptionStatus === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{displayStatus}</span>
      </div>
      <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{usedSeats} / {totalSeats} used</span><span>{pct}%</span></div>
      <ProgressBar value={usedSeats} max={totalSeats} color="bg-purple-500" />
      <div className="flex justify-between items-center mt-2">
        <p className="text-xs text-gray-400">Due: {formatDate(contractEndDate)}</p>
        <p className="text-xs font-medium text-gray-700">{remaining}d left</p>
      </div>
    </div>
  );
}

function DeptPerformance({ data }) {
  const departments = data.departmentPerformance;
  if (!departments || departments.length === 0) return <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full"><p className="text-sm font-semibold text-gray-700 mb-2">Department Performance</p><p className="text-xs text-gray-400">No data</p></div>;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">Department Performance</p>
      <div className="space-y-3">
        {departments.map((d, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex justify-between mb-1"><span className="text-sm text-gray-800 font-medium truncate">{d.name}</span><span className="text-sm font-semibold text-gray-900">{d.avgScore || 0}%</span></div>
              <ProgressBar value={d.avgScore || 0} color={d.avgScore >= 70 ? 'bg-green-500' : d.avgScore >= 50 ? 'bg-blue-500' : 'bg-orange-400'} />
            </div>
            <span className="text-xs text-gray-400 w-16 text-right">{d.learners} users</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopPerformers({ data }) {
  const performers = data.topPerformers;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">Top Performers</p>
      {(!performers || performers.length === 0) ? <p className="text-xs text-gray-400">No assessment data yet</p> : (
        <div className="space-y-2">{performers.map((p, i) => (
          <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</div>
            <div className="flex-1"><p className="text-sm font-medium text-gray-900">{p.firstName} {p.lastName}</p></div>
            <p className="text-sm font-semibold">{p.avgScore}%</p>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function AtRiskLearners({ data }) {
  const learners = data.atRiskLearners;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">At-Risk Learners</p>
      {(!learners || learners.length === 0) ? <p className="text-xs text-gray-400">No at-risk learners</p> : (
        <div className="space-y-2">{learners.map((r, i) => (
          <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-red-50/50 border border-red-100">
            <UserX size={14} className="text-red-400" />
            <div className="flex-1"><p className="text-sm text-gray-800">{r.firstName} {r.lastName}</p></div>
            <span className="text-sm font-semibold text-red-600">{r.avgScore}%</span>
          </div>
        ))}</div>
      )}
    </div>
  );
}

function ContentPerf({ data }) {
  const content = data.contentPerformance;
  if (!content || content.length === 0) return <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full"><p className="text-sm font-semibold text-gray-700 mb-2">Content Performance</p><p className="text-xs text-gray-400">No data</p></div>;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">Content Performance</p>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-gray-100 text-xs text-gray-400 uppercase"><th className="text-left py-2">Title</th><th className="text-center py-2">Enrolled</th><th className="text-center py-2">Avg Score</th></tr></thead>
        <tbody>{content.map((c, i) => (
          <tr key={i} className="border-b border-gray-50"><td className="py-2 text-gray-800 truncate max-w-[180px]">{c.title}</td><td className="py-2 text-center text-gray-600">{c.enrolled}</td><td className="py-2 text-center font-medium">{c.avgScore || 0}%</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function RecentActivity({ data }) {
  const activity = data.recentActivity;
  if (!activity || activity.length === 0) return <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full"><p className="text-sm font-semibold text-gray-700 mb-2">Recent Activity</p><p className="text-xs text-gray-400">No activity</p></div>;
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</p>
      <div className="space-y-2">{activity.map((a, i) => (
        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
          <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
          <p className="text-xs text-gray-700 truncate flex-1"><span className="font-medium">{a.firstName} {a.lastName}</span> {a.action}</p>
          <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(a.createdAt)}</span>
        </div>
      ))}</div>
    </div>
  );
}

function QuickAccess() {
  const navigate = useNavigate();
  const links = [
    { label: 'Add Learner', icon: Plus, path: '/app/learners', color: 'bg-blue-50 text-blue-600' },
    { label: 'Reports', icon: BarChart2, path: '/app/reports', color: 'bg-green-50 text-green-600' },
    { label: 'Announcements', icon: Megaphone, path: '/app/announcement', color: 'bg-purple-50 text-purple-600' },
    { label: 'Catalog', icon: BookOpen, path: '/app/catalog', color: 'bg-amber-50 text-amber-600' },
  ];
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 h-full">
      <p className="text-sm font-semibold text-gray-700 mb-3">Quick Access</p>
      <div className="grid grid-cols-2 gap-2">
        {links.map((l, i) => (
          <button key={i} onClick={() => navigate(l.path)} className="flex items-center gap-2 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors text-left">
            <div className={`${l.color} rounded-lg p-2`}><l.icon size={16} /></div>
            <span className="text-xs font-medium text-gray-700">{l.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Section Registry ─────────────────────────────────────────────────────────

const ALL_SECTIONS = [
  { id: 'stats', label: 'Stats Cards', component: StatsCards, span: 'full' },
  { id: 'quickAccess', label: 'Quick Access', component: QuickAccess, span: 'half' },
  { id: 'deptPerf', label: 'Department Performance', component: DeptPerformance, span: 'half' },
  { id: 'topPerformers', label: 'Top Performers', component: TopPerformers, span: 'half' },
  { id: 'atRisk', label: 'At-Risk Learners', component: AtRiskLearners, span: 'half' },
  { id: 'content', label: 'Content Performance', component: ContentPerf, span: 'half' },
  { id: 'activity', label: 'Recent Activity', component: RecentActivity, span: 'half' },
];

const DEFAULT_ORDER = ['stats', 'quickAccess', 'deptPerf', 'topPerformers', 'atRisk', 'content', 'activity'];

const LAYOUTS = [
  { key: 'grid', label: 'Grid', desc: '2-column responsive grid' },
  { key: 'stack', label: 'Stacked', desc: 'Full-width single column' },
  { key: 'compact', label: 'Compact', desc: 'Dense 3-column grid' },
  { key: 'gradient', label: 'Gradient Hero', desc: 'Hero banner + cards' },
];

function getStoredConfig() {
  try { const raw = localStorage.getItem(STORAGE_KEY); if (raw) return JSON.parse(raw); } catch {}
  return { layout: 'grid', order: DEFAULT_ORDER, hidden: [] };
}

// ─── Layout Renderers ─────────────────────────────────────────────────────────

function GridLayout({ sections, data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {sections.map(s => (
        <div key={s.id} className={s.span === 'full' ? 'lg:col-span-2' : ''}>
          <s.component data={data} />
        </div>
      ))}
    </div>
  );
}

function StackLayout({ sections, data }) {
  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {sections.map(s => <div key={s.id}><s.component data={data} /></div>)}
    </div>
  );
}

function CompactLayout({ sections, data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {sections.map(s => (
        <div key={s.id} className={s.span === 'full' ? 'md:col-span-2 xl:col-span-3' : ''}>
          <s.component data={data} />
        </div>
      ))}
    </div>
  );
}

function GradientLayout({ sections, data }) {
  const remaining = data.contractEndDate ? Math.max(0, Math.ceil((new Date(data.contractEndDate) - Date.now()) / 86400000)) : 0;
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-purple-700 to-indigo-700 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div><h2 className="text-lg font-semibold">Overview</h2><p className="text-purple-200 text-sm">Real-time performance</p></div>
          <div className="text-right">
            <span className="bg-white/20 text-xs px-3 py-1 rounded-full">{data.subscriptionStatus || 'N/A'}</span>
            <p className="text-purple-200 text-xs mt-1">{remaining}d remaining</p>
          </div>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { l: 'Learners', v: data.activeLearners },
            { l: 'Total', v: data.totalUsers },
            { l: 'Depts', v: data.departments },
            { l: 'Teams', v: data.teams },
            { l: 'Seats', v: `${data.usedSeats}/${data.totalSeats}` },
            { l: 'In Teams', v: data.learnersInTeams },
          ].map((k, i) => (
            <div key={i} className="bg-white/10 rounded-xl p-3 text-center"><p className="text-lg font-bold">{k.v}</p><p className="text-xs text-purple-200">{k.l}</p></div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sections.filter(s => s.id !== 'stats' && s.id !== 'licence').map(s => (
          <div key={s.id}><s.component data={data} /></div>
        ))}
      </div>
    </div>
  );
}

const LAYOUT_MAP = { grid: GridLayout, stack: StackLayout, compact: CompactLayout, gradient: GradientLayout };

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ClientDashboard() {
  const { userInfo } = useSelector((state) => state);
  const [editOpen, setEditOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    activeLearners: 0, totalUsers: 0, departments: 0, teams: 0, learnersInTeams: 0,
    totalSeats: 0, usedSeats: 0, subscriptionStatus: null, contractEndDate: null,
    topPerformers: [], atRiskLearners: [], departmentPerformance: [], contentPerformance: [], recentActivity: [],
  });
  const [config, setConfig] = useState(getStoredConfig);
  const [showRestrictedToast, setShowRestrictedToast] = useState(false);

  useEffect(() => {
    setLoading(true);
    getDashboardStats().then(res => {
      if (res.ok) {
        setData(res.data);
        if (res.data.subscriptionStatus === 'RESTRICTED') setShowRestrictedToast(true);
        if (res.data.contractEndDate && new Date(res.data.contractEndDate) < new Date()) setShowExpiredToast(true);
      }
    }).finally(() => setLoading(false));
  }, []);
  const [showExpiredToast, setShowExpiredToast] = useState(false);

  const saveConfig = (c) => { setConfig(c); localStorage.setItem(STORAGE_KEY, JSON.stringify(c)); };
  const visibleSections = config.order.filter(id => !config.hidden.includes(id)).map(id => ALL_SECTIONS.find(s => s.id === id)).filter(Boolean);
  const LayoutComp = LAYOUT_MAP[config.layout] || GridLayout;

  return (
    <Page title="Dashboard">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-xl font-bold text-gray-900">Dashboard</h1><p className="text-sm text-gray-500 mt-0.5">Overview of your organisation</p></div>
          <button onClick={() => setEditOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm">
            <Settings2 size={16} /> Customize
          </button>
        </div>

        {loading ? (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 animate-pulse"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-200 rounded-lg" /><div className="space-y-2"><div className="h-5 w-16 bg-gray-200 rounded" /><div className="h-3 w-24 bg-gray-100 rounded" /></div></div></div>)}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 animate-pulse"><div className="h-4 w-40 bg-gray-200 rounded mb-4" />{[1,2,3].map(j => <div key={j} className="h-8 bg-gray-100 rounded mb-2" />)}</div>)}</div>
          </div>
        ) : (
          <LayoutComp sections={visibleSections} data={data} />
        )}
      </div>

      {/* Right Side Drawer */}
      <Drawer anchor="right" open={editOpen} onClose={() => setEditOpen(false)} PaperProps={{ sx: { width: 320 } }}>
        <CustomizePanel config={config} onSave={(c) => { saveConfig(c); setEditOpen(false); }} onReset={() => { saveConfig({ layout: 'grid', order: DEFAULT_ORDER, hidden: [] }); setEditOpen(false); }} onClose={() => setEditOpen(false)} />
      </Drawer>

      {showRestrictedToast && (
        <div className="fixed top-20 right-6 z-[1300] max-w-sm bg-red-50 border border-red-200 rounded-xl shadow-lg p-4 flex items-start gap-3 animate-[slideIn_0.3s_ease]">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Your trial period has ended</p>
            <p className="text-xs text-red-600 mt-0.5">Renew your subscription to continue accessing all features. Please make payment to restore access.</p>
          </div>
          <button onClick={() => setShowRestrictedToast(false)} className="p-1 rounded hover:bg-red-100 flex-shrink-0">
            <X size={16} className="text-red-400" />
          </button>
        </div>
      )}
      {showExpiredToast && (
        <div className="fixed top-20 right-6 z-[1300] max-w-sm bg-amber-50 border border-amber-200 rounded-xl shadow-lg p-4 flex items-start gap-3 animate-[slideIn_0.3s_ease]">
          <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Your plan has expired</p>
            <p className="text-xs text-amber-700 mt-0.5">Please reach out to your super admin or contact support to renew your subscription.</p>
          </div>
          <button onClick={() => setShowExpiredToast(false)} className="p-1 rounded hover:bg-amber-100 flex-shrink-0">
            <X size={16} className="text-amber-400" />
          </button>
        </div>
      )}
    </Page>
  );
}

// ─── Customize Side Panel ─────────────────────────────────────────────────────

function CustomizePanel({ config, onSave, onReset, onClose }) {
  const [layout, setLayout] = useState(config.layout);
  const [order, setOrder] = useState(config.order);
  const [hidden, setHidden] = useState(config.hidden);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOver.current = idx; };
  const handleDragEnd = () => {
    const list = [...order];
    const item = list.splice(dragItem.current, 1)[0];
    list.splice(dragOver.current, 0, item);
    dragItem.current = null;
    dragOver.current = null;
    setOrder(list);
  };
  const toggleVisibility = (id) => setHidden(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Customize Dashboard</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Layout Selector */}
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Layout</p>
        <div className="grid grid-cols-2 gap-2 mb-5">
          {LAYOUTS.map(l => (
            <button key={l.key} onClick={() => setLayout(l.key)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${layout === l.key ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <p className="text-xs font-medium text-gray-900">{l.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{l.desc}</p>
              {layout === l.key && <Check size={12} className="text-purple-600 mt-1" />}
            </button>
          ))}
        </div>

        {/* Section Order */}
        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Sections (drag to reorder)</p>
        <div className="space-y-1">
          {order.map((id, idx) => {
            const section = ALL_SECTIONS.find(s => s.id === id);
            if (!section) return null;
            const isHidden = hidden.includes(id);
            return (
              <div key={id} draggable onDragStart={() => handleDragStart(idx)} onDragEnter={() => handleDragEnter(idx)} onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-grab active:cursor-grabbing ${isHidden ? 'border-gray-100 bg-gray-50 opacity-50' : 'border-gray-200 bg-white'}`}>
                <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
                <span className={`flex-1 text-sm ${isHidden ? 'text-gray-400' : 'text-gray-800 font-medium'}`}>{section.label}</span>
                <button onClick={() => toggleVisibility(id)} className="p-1 rounded hover:bg-gray-100">
                  {isHidden ? <EyeOff size={14} className="text-gray-300" /> : <Eye size={14} className="text-gray-500" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-gray-200 px-4 py-4 space-y-2">
        <button onClick={() => onSave({ layout, order, hidden })} className="w-full px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700">Save</button>
        <button onClick={onReset} className="w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50">Reset to Default</button>
      </div>
    </div>
  );
}
